import type { Signal } from '@angular/core'
import { Injectable, signal } from '@angular/core'
import { User } from '@awork/features/user/models/user.model'
import type { Absence } from '@awork/features/user/models/absence.model'
import { EntitySignalStore } from '@awork/core/state/signal-store/entitySignalStore'

@Injectable({ providedIn: 'root' })
export class UserStore extends EntitySignalStore<User> {
  private readonly visitedUserId = signal<string | null>(null)

  constructor() {
    super({
      entityConstructor: user => new User(user, 'UserStore.constructor'),
      name: 'user',
      nullableProps: ['capacityPerWeek']
    })
  }

  /**
   * Sets the currently visited user profile
   * @param {string | null} id
   */
  setVisitedUser(id: string | null): void {
    this.visitedUserId.set(id)
  }

  /**
   * Gets the currently visited user profile id
   * @returns {Signal<string | null>}
   */
  getVisitedUserId(): Signal<string | null> {
    return this.visitedUserId.asReadonly()
  }

  preUpdateEntity(prevEntity: Readonly<User>, nextEntity: Readonly<User>): User {
    const absences = nextEntity.absences || prevEntity.absences || []

    return { ...nextEntity, absences } as User
  }

  /**
   * Sets the current user
   * @param {string} id
   */
  setCurrentUser(id: string): void {
    this.setActive(id)
  }

  /**
   * Delete absence
   * @param {Absence} absence
   */
  deleteAbsence(absence: Absence): void {
    this.update(absence.userId, user => ({
      absences: user.absences?.filter(a => a.id !== absence.id) || []
    }))
  }

  /**
   * Delete a workspace absence
   * @param {string} workspaceAbsenceId
   */
  deleteWorkspaceAbsence(workspaceAbsenceId: string): void {
    this.update(
      user => user.absences?.some(absence => absence.id === workspaceAbsenceId),
      user => ({ absences: user.absences?.filter(a => a.id !== workspaceAbsenceId) || [] })
    )
  }

  /**
   * Sync user's absence.
   * @param {Absence} absence
   */
  syncAbsence(absence: Absence): void {
    this.update(absence.userId, user => ({
      absences: [...(user.absences?.filter(a => a.id !== absence.id) || []), absence]
    }))
  }
}
